import { motion } from "framer-motion";
import { FaCode, FaServer, FaRobot, FaShieldAlt } from "react-icons/fa";
import resumeData from "../../data/resumeData";

const iconMap = {
  "Frontend Platforms": FaCode,
  "Backend & Cloud": FaServer,
  "Security & Identity": FaShieldAlt,
  "AI & Leadership": FaRobot,
};

const ExpertiseSection = ({ darkMode = false }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section
      id="expertise"
      className={`relative overflow-hidden py-14 sm:py-16 transition-colors duration-500 ${
        darkMode ? "bg-stone-900" : "bg-white"
      }`}
    >
      <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.4 }}
          className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-end"
        >
          <div>
            <p className="text-xs font-black uppercase tracking-[0.24em] text-teal-800 dark:text-teal-300">
              Expertise
            </p>
            <h2 className="mt-3 max-w-3xl text-3xl font-black leading-tight text-stone-950 dark:text-white sm:text-4xl">
              Core strengths across stack, cloud, security, and AI.
            </h2>
          </div>
          <div className="border-l-4 border-teal-600 pl-5">
            <p className="max-w-2xl text-sm leading-7 text-stone-700 dark:text-stone-300">
              Full-stack engineering with identity-aware security, cloud-native delivery, and applied AI
              exploration — across insurance, banking, and energy & agriculture
              ({resumeData.industries.flatMap((item) => item.clients).join(", ")}).
            </p>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          transition={{ staggerChildren: 0.06 }}
          className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
        >
          {resumeData.expertise.map(({ title, items }) => {
            const Icon = iconMap[title] ?? FaCode;
            return (
              <motion.article
                key={title}
                variants={cardVariants}
                className={`rounded-2xl border p-5 ${
                  darkMode
                    ? "border-stone-700 bg-stone-800/50"
                    : "border-stone-950/10 bg-[#f8f6f1]"
                }`}
              >
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                    darkMode ? "bg-stone-700 text-teal-400" : "bg-stone-950 text-teal-200"
                  }`}
                >
                  <Icon size={18} />
                </span>
                <h3 className={`mt-4 text-lg font-black ${darkMode ? "text-white" : "text-stone-950"}`}>
                  {title}
                </h3>
                <ul
                  className={`mt-3 flex flex-wrap gap-1.5 text-xs font-semibold ${
                    darkMode ? "text-stone-300" : "text-stone-700"
                  }`}
                >
                  {items.map((item) => (
                    <li
                      key={item}
                      className={`rounded-full px-2.5 py-1 ring-1 ${
                        darkMode
                          ? "bg-stone-700 ring-stone-600"
                          : "bg-white ring-stone-950/10"
                      }`}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ExpertiseSection;
